import React, { useCallback, useEffect, useMemo } from 'react';

import { useControllableState } from './hooks/useControllableState';
import type { SidebarCtx } from './SidebarContext';
import { SidebarContext } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
interface SidebarProviderProps {
  children: React.ReactNode;
  /**
   * Whether the sidebar should initially render in an open state.
   *
   * Does not control subsequent interaction with the sidebar, only the initial render.
   *
   * @default true
   */
  defaultExpanded?: boolean;
  /**
   * Used to control the state of the sidebar externally.
   *
   * **NOTE**: Must be used in conjunction with `onExpandedChange`
   */
  expanded?: boolean;
  /**
   * Whether the sidebar can be collapsed
   *
   * @default false
   */
  isCollapsible?: boolean;
  /**
   * Trigger opening the sidebar via a global keyboard shortcut
   *
   * NOTE: If rendering multiple sidebars at the same time, be sure to assign each a unique shortcut
   */
  keyboardShortcut?: {
    /**
     * Which key should trigger the sidebar
     */
    key: string;

    /**
     * Whether the command key (`⌘`) or control key (`ctrl`) should be pressed as well
     *
     * @default true
     */
    withMetaKey?: boolean;
  };
  /**
   * Callback for triggering side effects on sidebar state change.
   */
  onExpandedChange?: (expanded: boolean) => void;
}

const NoOp = () => {};

// ============================================================
//     PROVIDER
// ============================================================
/**
 * Provides context for sidebar state and actions
 *
 * Ported from the Frontend-Platform Sidebar provider, minus mobile drawer support
 * (Daffy's dashboard has no mobile mode). The context keeps the `isMobile` /
 * `isOpenMobile` fields so consumers stay diffable against the FE2.0 source.
 */
export const SidebarProvider = (props: SidebarProviderProps) => {
  const {
    children,
    defaultExpanded = true,
    expanded: expandedProp,
    isCollapsible = false,
    keyboardShortcut,
    onExpandedChange: onExpandedChangeProp,
  } = props;

  const [isExpanded, setIsExpanded] = useControllableState({
    defaultProp: defaultExpanded,
    prop: isCollapsible ? expandedProp : true,
    onChange: isCollapsible ? onExpandedChangeProp : NoOp,
  });

  // Helper to toggle the sidebar.
  const toggleSidebar = useCallback(() => {
    setIsExpanded(old => !old);
  }, [setIsExpanded]);

  // Handles a keyboard shortcut to toggle the sidebar
  useEffect(() => {
    if (!keyboardShortcut) return undefined;

    const { key, withMetaKey = true } = keyboardShortcut;

    const handleKeyDown = (event: KeyboardEvent) => {
      const shortcutKeyPressed = event.key === key;
      const metaKeyPressed = withMetaKey ? event.metaKey || event.ctrlKey : true;

      if (shortcutKeyPressed && metaKeyPressed) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [keyboardShortcut, toggleSidebar]);

  const contextValue = useMemo<SidebarCtx>(
    () => ({
      isExpanded,
      isMobile: false,
      isOpenMobile: false,
      setIsExpanded,
      setIsOpenMobile: NoOp,
      toggleSidebar,
    }),
    [isExpanded, setIsExpanded, toggleSidebar],
  );

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
};

SidebarProvider.displayName = 'Sidebar.Provider' as const;
